import { memo, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MapPinIcon, ClockIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { getCategoryIcon, getCategoryColor, getCategoryIconColor } from '../utils/entityImageUtils'
import { formatTimeAgo } from '../utils/formatters'
import { hideImageOnError } from '../hooks/useImageErrorHandler'
import galleryCache from '../services/galleryCache'
import { useDistanceUnit } from '../hooks/useDistanceUnit'

/**
 * Memoized search results dropdown to prevent unnecessary re-renders
 * Uses fixed positioning to avoid being clipped by parent overflow
 */
const SearchResultsDropdown = memo(function SearchResultsDropdown({ 
  results, 
  onSelectEntity, 
  showDropdown,
  inputRef,
  onClose,
  searchLocation = '',
  searchLocationName = '',
  radius = 50,
  onClearLocation = null,
  query = ''
}) {
  const { t } = useTranslation()
  const { formatDistance } = useDistanceUnit()
  const dropdownRef = useRef(null)
  const [position, setPosition] = useState({ top: 0, bottom: 0, left: 0, width: 0, openUpward: false, maxHeight: 400 })
  const [galleryImages, setGalleryImages] = useState({}) // entityId -> primary image URL

  // Fetch gallery images for search results
  useEffect(() => {
    if (!results || results.length === 0) return

    const fetchGalleryImages = async () => {
      const imageMap = {}
      await Promise.all(
        results.map(async (entity) => {
          try {
            const gallery = await galleryCache.getGallery(entity.id)
            const primaryImage = gallery.find(img => img.isPrimary)
            const imageToUse = primaryImage || gallery[0]
            if (imageToUse) {
              imageMap[entity.id] = imageToUse.imageUrl
            }
          } catch (error) {
            // Silently fail - will show category icon instead
          }
        })
      )
      setGalleryImages(imageMap)
    }

    fetchGalleryImages()
  }, [results])

  useEffect(() => {
    if (!showDropdown || !inputRef?.current) return

    const updatePosition = () => {
      if (!inputRef?.current) return
      
      const inputRect = inputRef.current.getBoundingClientRect()
      const DROPDOWN_MAX_HEIGHT = 400
      const spaceBelow = window.innerHeight - inputRect.bottom - 8
      const spaceAbove = inputRect.top - 8
      const openUpward = spaceBelow < 200 && spaceAbove > spaceBelow

      setPosition({
        top: inputRect.bottom + 4,
        bottom: window.innerHeight - inputRect.top + 4,
        left: inputRect.left,
        width: inputRect.width,
        openUpward,
        maxHeight: Math.min(DROPDOWN_MAX_HEIGHT, openUpward ? spaceAbove : spaceBelow)
      })
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [showDropdown, inputRef])

  const hasLocation = !!(searchLocationName || searchLocation)
  const hasResults = results.length > 0

  // Show dropdown if there are results, OR if there's a location scope to show (banner + no-results)
  if (!showDropdown || (!hasResults && !hasLocation)) {
    return null
  }

  const dropdown = (
    <div 
      id="search-results-dropdown"
      ref={dropdownRef}
      className="fixed z-[1000] bg-white/95 backdrop-blur-md border border-primary/20 rounded-2xl shadow-2xl overflow-y-auto scrollbar-thin"
      style={{
        top: position.openUpward ? 'auto' : `${position.top}px`,
        bottom: position.openUpward ? `${position.bottom}px` : 'auto',
        left: `${position.left}px`,
        width: `${position.width}px`,
        maxHeight: `${position.maxHeight}px`
      }}
    >
      {/* Header: location scope + close button */}
      {hasLocation && (
        <div className="flex items-start gap-2 px-3 pt-2.5 pb-2">
          <div className="flex-1 min-w-0 bg-primary/5 border border-primary/15 rounded-xl px-2.5 py-2">
            {/* Row 1: pin + location name */}
            <div className="flex items-center gap-1.5 min-w-0">
              <MapPinIcon className="w-3.5 h-3.5 text-primary flex-shrink-0" />
              <span className="text-xs font-semibold text-gray-900 min-w-0 truncate">
                {searchLocationName || searchLocation}
              </span>
            </div>
            {/* Row 2: radius */}
            <div className="flex items-center gap-1 mt-0.5 pl-5">
              <span className="text-[11px] text-primary font-medium">{formatDistance(radius)}</span>
              <span className="text-[11px] text-gray-400">{t('map.radius', 'radius')}</span>
            </div>
            {/* Row 2: clear button */}
            {onClearLocation && (
              <button
                onClick={() => { onClearLocation(); onClose?.() }}
                className="mt-1.5 text-[11px] text-gray-500 hover:text-primary font-medium transition-colors flex items-center gap-1"
              >
                <XMarkIcon className="w-3 h-3" />
                {t('map.searchEverywhere', 'Search everywhere')}
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1 transition-colors mt-0.5"
            title={t('app.cancel', 'Close')}
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Divider below header — only when header is shown */}
      {hasLocation && <div className="mx-3 border-t border-primary/10" />}

      {/* No results state */}
      {!hasResults && (
        <div className="mx-3 mt-2 mb-1 bg-gray-50 border border-gray-200/50 rounded-md px-3 py-2 shadow-sm">
          <p className="text-gray-700 text-xs">{t('map.noTrucksFound', { query })}</p>
          {hasLocation && onClearLocation && (
            <p className="mt-1 text-xs text-gray-400">
              {t('map.noResultsHint')}
            </p>
          )}
        </div>
      )}

      <div className="p-2">
        {results.map((entity, index) => {
          const lastSeen = entity.last_seen_at || entity.lastSeenAt
          const address = entity.current_address || entity.currentAddress
          const timeAgo = formatTimeAgo(lastSeen)
          
          return (
            <button
              key={entity.id}
              onClick={() => onSelectEntity(entity)}
              className={`w-full p-3 text-left rounded-xl hover:bg-primary/5 focus:outline-none focus:bg-primary/10 transition-all group ${index > 0 ? 'mt-2' : ''}`}
            >
              <div className="flex items-start gap-3">
                {/* Entity Image or Icon */}
                <div className="flex-shrink-0">
                  {(() => {
                    const IconComponent = getCategoryIcon(entity.category)
                    const bgColor = getCategoryColor(entity.category)
                    const iconColor = getCategoryIconColor(entity.category)
                    // Use primary image from gallery (same as map popup), fallback to entity image
                    const imageUrl = galleryImages[entity.id] || entity.image_url || entity.imageUrl
                    
                    // Only render image if URL exists and is not empty
                    return (imageUrl && imageUrl.trim() !== '') ? (
                      <div className="relative w-12 h-12 rounded-lg overflow-hidden">
                        <img 
                          src={imageUrl} 
                          alt={entity.name}
                          className="w-12 h-12 rounded-lg object-cover border-2 border-primary/20"
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextElementSibling.classList.remove('hidden')
                            e.target.nextElementSibling.classList.add('flex')
                          }}
                        />
                        <div className={`absolute inset-0 w-12 h-12 rounded-lg ${bgColor} border-2 border-primary/20 hidden items-center justify-center`}>
                          <IconComponent className={`w-6 h-6 ${iconColor}`} />
                        </div>
                      </div>
                    ) : (
                      <div className={`w-12 h-12 rounded-lg ${bgColor} border-2 border-primary/20 flex items-center justify-center`}>
                        <IconComponent className={`w-6 h-6 ${iconColor}`} />
                      </div>
                    )
                  })()}
                </div>
                
                {/* Entity Info */}
                <div className="flex-1 min-w-0">
                  {/* Name */}
                  <div className="font-semibold text-sm text-gray-900 truncate group-hover:text-primary transition-colors">
                    {entity.name}
                  </div>
                  
                  {/* Category */}
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-primary/10 text-primary">
                      {t(`entities.categories.${entity.type || entity.category}`, 
                         entity.type?.replace('_', ' ') || entity.category?.replace('_', ' ') || t('entities.categories.food_truck', 'Food Truck'))}
                    </span>
                  </div>
                  
                  {/* Last Seen Location & Time */}
                  {(address || timeAgo) && (
                    <div className="mt-2 space-y-1">
                      {address && (
                        <div className="flex items-start gap-1.5 text-xs text-gray-600">
                          <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-primary/60" />
                          <span className="truncate">{address}</span>
                        </div>
                      )}
                      {timeAgo && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <ClockIcon className="w-3.5 h-3.5 flex-shrink-0 text-primary/60" />
                          <span>{t('entities.detail.lastSeen', 'Last seen:')} {timeAgo}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )

  return createPortal(dropdown, document.body)
})

export default SearchResultsDropdown
